import React from "react"
import StaticTimer from "@/Components/StaticTimer"
import { GameTimerKey, useGameTimer } from "@/utils/gameTimers"

interface GameTimerProps {
  /** Which of the server's clocks this ring draws. */
  timerKey: GameTimerKey
  totalTime: number
  /** Shown until the first value for this clock arrives. Defaults to `totalTime`. */
  fallback?: number
  size?: "sm" | "md" | "lg" | "xl" | "2lg"
  showLabel?: boolean
  labelText?: string
  className?: string
  timeClassName?: string
}

/**
 * A countdown ring that reads the clock itself.
 *
 * The point of it is where the subscription lives: the seconds are the one
 * thing on screen that changes every second, so only the ring should re-render
 * when they do. A parent that passed `currentTime` down would have to subscribe
 * to the tick as well, and would drag its whole subtree along once a second —
 * which for Board meant redrawing all 40 cells to move one number. Anything
 * that just wants to *show* a clock renders this instead.
 *
 * Components that need the number for something other than drawing it (gating a
 * modal, sizing a warning) still read `useGameTimer` directly, and re-render on
 * the tick by necessity.
 */
const GameTimer: React.FC<GameTimerProps> = ({
  timerKey,
  totalTime,
  fallback,
  ...rest
}) => {
  const seconds = useGameTimer(timerKey)
  return (
    <StaticTimer
      totalTime={totalTime}
      currentTime={seconds ?? fallback ?? totalTime}
      {...rest}
    />
  )
}

export default React.memo(GameTimer)
